package com.bulut.mecitogullari.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.util.daysInMonth
import com.bulut.mecitogullari.core.util.epochMillisToLocalDate
import com.bulut.mecitogullari.core.util.formatDate
import com.bulut.mecitogullari.core.util.monthName
import com.bulut.mecitogullari.core.util.todayLocalDate
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.core.util.toUtcMidnightMillis
import com.bulut.mecitogullari.ui.theme.AppColors
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number

/**
 * Read-only date field; tapping opens a wheel-based day/month/year picker
 * (same pattern as [DropdownField]). Values are UTC-midnight epoch millis.
 */
@Composable
fun DateField(
    label: String,
    dateEpochMillis: Long?,
    onDateSelected: (Long) -> Unit,
    modifier: Modifier = Modifier,
) {
    var showPicker by remember { mutableStateOf(false) }

    Box(modifier = modifier) {
        OutlinedTextField(
            value = dateEpochMillis?.let { formatDate(it) } ?: "",
            onValueChange = {},
            readOnly = true,
            label = { Text(label) },
            trailingIcon = { Icon(Icons.Default.DateRange, contentDescription = null, tint = AppColors.TextSecondary) },
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(12.dp),
        )
        Box(modifier = Modifier.matchParentSize().clickable { showPicker = true })
    }

    if (showPicker) {
        WheelDatePickerDialog(
            title = label,
            initialDateEpochMillis = dateEpochMillis,
            onConfirm = { millis ->
                onDateSelected(millis)
                showPicker = false
            },
            onDismiss = { showPicker = false },
        )
    }
}

/** Day / month / year wheels side by side, iOS style. */
@Composable
fun WheelDatePickerDialog(
    title: String,
    initialDateEpochMillis: Long?,
    onConfirm: (Long) -> Unit,
    onDismiss: () -> Unit,
) {
    val initialDate = remember {
        epochMillisToLocalDate(initialDateEpochMillis ?: todayUtcMidnightMillis())
    }
    var day by remember { mutableStateOf(initialDate.day) }
    var month by remember { mutableStateOf(initialDate.month.number) }
    var year by remember { mutableStateOf(initialDate.year) }

    val years = remember {
        val thisYear = todayLocalDate().year
        val from = minOf(thisYear - 15, initialDate.year)
        val to = maxOf(thisYear + 15, initialDate.year)
        (from..to).toList()
    }

    val maxDay = daysInMonth(month, year)
    LaunchedEffect(maxDay) {
        if (day > maxDay) day = maxDay
    }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(modifier = Modifier.padding(top = 20.dp, bottom = 8.dp)) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.padding(horizontal = 24.dp),
                )

                Box(modifier = Modifier.fillMaxWidth().padding(top = 8.dp)) {
                    // Single highlight bar behind all three wheels
                    Box(
                        modifier = Modifier
                            .align(Alignment.Center)
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp)
                            .height(ITEM_HEIGHT)
                            .background(AppColors.SurfaceMuted, RoundedCornerShape(12.dp)),
                    )
                    Row(modifier = Modifier.fillMaxWidth()) {
                        // Day wheel is rebuilt when the month length changes.
                        key(maxDay) {
                            Wheel(
                                options = (1..maxDay).map { it.toString() },
                                initialIndex = (day.coerceAtMost(maxDay)) - 1,
                                onCenteredIndexChange = { day = it + 1 },
                                showCenterHighlight = false,
                                modifier = Modifier.weight(0.8f),
                            )
                        }
                        Wheel(
                            options = (1..12).map { monthName(it) },
                            initialIndex = month - 1,
                            onCenteredIndexChange = { month = it + 1 },
                            showCenterHighlight = false,
                            modifier = Modifier.weight(1.3f),
                        )
                        Wheel(
                            options = years.map { it.toString() },
                            initialIndex = years.indexOf(year).coerceAtLeast(0),
                            onCenteredIndexChange = { year = years[it] },
                            showCenterHighlight = false,
                            modifier = Modifier.weight(1f),
                        )
                    }
                }

                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                    horizontalArrangement = Arrangement.End,
                ) {
                    TextButton(onClick = onDismiss) { Text(LocalStrings.current.cancel) }
                    TextButton(
                        onClick = {
                            val safeDay = day.coerceAtMost(daysInMonth(month, year))
                            onConfirm(LocalDate(year, month, safeDay).toUtcMidnightMillis())
                        },
                    ) {
                        Text("Tamam", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}
